import { useEffect } from "react";
import websocketService from "services/websocket";
import { applyLineTick } from "./applyLineTick";
import { applyCandleTick } from "./applyCandleTick";
import type { ServerBasePrice } from "services/types";
import type { CandleWithFlag } from "../model/types";

interface UseChartWebSocketParams {
  symbol: string;
  timeframeMs: number;
  chartType: "line" | "candlestick" | "histogram";
  dataRef: React.MutableRefObject<(ServerBasePrice | CandleWithFlag)[]>;
  animatedPriceRef: React.MutableRefObject<number | null>;
  animationStartTimeRef: React.MutableRefObject<number>;
  animatingCandleIndexRef: React.MutableRefObject<number | null>;
  liveCandlePrice: React.MutableRefObject<number | null>;
  liveLinePriceRef: React.MutableRefObject<number | null>;
  prevHighRef: React.MutableRefObject<number | null>;
  prevLowRef: React.MutableRefObject<number | null>;
  highChangedRef: React.MutableRefObject<boolean>;
  lowChangedRef: React.MutableRefObject<boolean>;
  onTick: () => void;
}

export function useChartWebSocket({
  symbol,
  timeframeMs,
  chartType,
  dataRef,
  animatedPriceRef,
  animationStartTimeRef,
  animatingCandleIndexRef,
  liveCandlePrice,
  liveLinePriceRef,
  prevHighRef,
  prevLowRef,
  highChangedRef,
  lowChangedRef,
  onTick,
}: UseChartWebSocketParams) {
  useEffect(() => {
    if (!symbol) return;

    const unsub = websocketService.subscribePrice(
      symbol,
      ({ price, timestamp }) => {
        if (chartType === "line") {
          applyLineTick({
            dataRef,
            timeframeMs,
            price,
            timestamp,
            animatedPriceRef,
            animationStartTimeRef,
            liveLinePriceRef,
          });
        } else {
          applyCandleTick({
            dataRef,
            timeframeMs,
            price,
            timestamp,
            animatedPriceRef,
            animationStartTimeRef,
            animatingCandleIndexRef,
            liveCandlePrice,
            prevHighRef,
            prevLowRef,
            highChangedRef,
            lowChangedRef,
          });
        }

        onTick();
      },
    );

    return unsub;
  }, [symbol, timeframeMs, chartType]);
}
